import { toast } from "react-toastify";
import api from "../../Utils/Axios";

// Custom hook for bill handlers - same structure, no changes
export const useBillHandlers = ({
  billFormData,
  setBillFormData,
  setShowBillModal,
  setShowEditBillModal,
  editingBill,
  setEditingBill,
  companyLanguage,
  setIsSaving,
  ledgerParties,
  fetchBills,
  getToken,
  setExpenseFields,
  defaultExpenseFields
}) => {

  // Handle bill form change
  const handleBillFormChange = (e) => {
    setBillFormData({
      ...billFormData,
      [e.target.name]: e.target.value
    });
  };

  // Customer dropdown change (ledger party → bill customer fields)
  const handleBillCustomerChange = (e) => {
    const partyId = e.target.value;
    const party = ledgerParties.find((p) => p._id === partyId);
    setBillFormData((prev) => ({
      ...prev,
      customerPartyId: partyId,
      customerName: party ? party.name : '',
      customerNameUrdu: party ? (party.nameUrdu || party.name) : ''
    }));
  };

  // Handle expense field change
  const handleExpenseChange = (fieldName, value) => {
    setBillFormData({
      ...billFormData,
      expenses: {
        ...billFormData.expenses,
        [fieldName]: parseFloat(value) || 0
      }
    });
  };

  // Handle sales field change
  const handleSalesChange = (fieldName, value) => {
    setBillFormData({
      ...billFormData,
      sales: {
        ...billFormData.sales,
        [fieldName]: parseFloat(value) || 0
      }
    });
  };

  // Move focus between expense fields on Enter key
  const handleExpenseKeyDown = (e, index) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      const nextIndex = index + 1;
      const nextExpenseInput = document.getElementById(`expense-input-${nextIndex}`);

      if (nextExpenseInput) {
        // اگلا خرچ والا فیلڈ
        nextExpenseInput.focus();
      } else {
        // اگر خرچ کے سارے فیلڈ بھر گئے تو سیلز والے پہلے فیلڈ پر جائیں
        const firstSalesInput = document.getElementById('sales-input-0');
        if (firstSalesInput) {
          firstSalesInput.focus();
        }
      }
    }
  };

  // Move focus between sales fields on Enter key
  const handleSalesKeyDown = (e, index) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      const nextIndex = index + 1;
      const nextSalesInput = document.getElementById(`sales-input-${nextIndex}`);
      if (nextSalesInput) {
        nextSalesInput.focus();
      }
    }
  };

  // Create bill
  const handleCreateBill = async (e) => {
    e.preventDefault();
    try {
      setIsSaving(true);
      const token = getToken();
      const response = await api.post("/bill/company/create", billFormData, {
        headers: {
          Authorization: `Bearer ${token}`
        }
      });

      const createdBill = response.data?.bill;

      // Note: Ledger transactions are automatically created by the backend when bill is created
      // No need to create them here to avoid duplicates

      toast.success(companyLanguage === 'ur' ? 'بل بن گیا!' : "Bill created successfully!");
      setShowBillModal(false);
      setBillFormData({
        serialNumber: '',
        customerPartyId: '',
        customerName: '',
        customerNameUrdu: '',
        date: new Date().toLocaleDateString('en-CA'),
        receivedMessage: 'Received, thanks for selling',
        receivedMessageUrdu: 'گاڑی نمبر',
        goodsMessage: 'Your goods via consignment/bill of lading',
        goodsMessageUrdu: 'آپکا مال بذریعہ بلٹی',
        expenses: {},
        sales: {}
      });
      fetchBills();
    } catch (error) {
      console.error("Create bill error:", error);
      toast.error(error.response?.data?.message || "Failed to create bill");
    } finally {
      setIsSaving(false);
    }
  };

  // Edit bill
  const handleEditBill = (bill) => {
    setEditingBill(bill);

    // Map expenses properly - handle different key variations
    const mappedExpenses = {};
    const billExpenses = bill.expenses || {};

    // Convert Map to object if needed
    let expensesObj = billExpenses;
    if (billExpenses instanceof Map) {
      expensesObj = Object.fromEntries(billExpenses);
    } else if (billExpenses && typeof billExpenses === 'object' && billExpenses.get && typeof billExpenses.get === 'function') {
      // Map-like object
      expensesObj = {};
      if (billExpenses.forEach) {
        billExpenses.forEach((val, key) => {
          expensesObj[key] = val;
        });
      }
    }

    // Map all expense values - try multiple key variations
    if (bill.expenseFields && Array.isArray(bill.expenseFields)) {
      bill.expenseFields.forEach(field => {
        const fieldName = field.fieldName;
        const fieldNameUrdu = field.fieldNameUrdu;

        // Try multiple key variations
        let value = expensesObj[fieldName];
        if (value === undefined || value === null) {
          value = expensesObj[fieldNameUrdu];
        }
        // Special cases: Labor -> Wages, Miscellaneous -> Mashiyana
        if (value === undefined || value === null) {
          if (fieldName === 'Labor') {
            value = expensesObj['Wages'] || expensesObj['مزدوری'];
          } else if (fieldName === 'Miscellaneous') {
            value = expensesObj['Mashiyana'] || expensesObj['مشیانہ'];
          }
        }

        if (value !== undefined && value !== null) {
          mappedExpenses[fieldName] = typeof value === 'string' ? parseFloat(value) || 0 : value;
        }
      });
    }

    // Also handle cases where expenses might be stored with alternate keys
    // Map Wages -> Labor, Mashiyana -> Miscellaneous
    if (expensesObj['Wages'] !== undefined && mappedExpenses['Labor'] === undefined) {
      mappedExpenses['Labor'] = typeof expensesObj['Wages'] === 'string' ? parseFloat(expensesObj['Wages']) || 0 : expensesObj['Wages'];
    }
    if (expensesObj['Mashiyana'] !== undefined && mappedExpenses['Miscellaneous'] === undefined) {
      mappedExpenses['Miscellaneous'] = typeof expensesObj['Mashiyana'] === 'string' ? parseFloat(expensesObj['Mashiyana']) || 0 : expensesObj['Mashiyana'];
    }

    // Also include any other expense keys that might not be in expenseFields
    Object.keys(expensesObj).forEach(key => {
      if (!mappedExpenses[key] && expensesObj[key] !== undefined && expensesObj[key] !== null) {
        mappedExpenses[key] = typeof expensesObj[key] === 'string' ? parseFloat(expensesObj[key]) || 0 : expensesObj[key];
      }
    });

    setBillFormData({
      serialNumber: bill.serialNumber || '',
      customerPartyId: bill.customerPartyId || '',
      customerName: bill.customerName || '',
      customerNameUrdu: bill.customerNameUrdu || '',
      date: bill.date ? new Date(bill.date).toLocaleDateString('en-CA') : new Date().toLocaleDateString('en-CA'),
      receivedMessage: bill.receivedMessage || 'Received, thanks for selling',
      receivedMessageUrdu: bill.receivedMessageUrdu || 'گاڑی نمبر',
      goodsMessage: bill.goodsMessage || 'Your goods via consignment/bill of lading',
      goodsMessageUrdu: bill.goodsMessageUrdu || 'آپکا مال بذریعہ بلٹی',
      vehicleNumber: expensesObj['Vehicle'] || '',
      actualVehicleNumber: expensesObj['ActualVehicle'] || '',
      itemName: expensesObj['ItemName'] || '',
      itemUnit: expensesObj['ItemUnit'] || '',
      itemQuantity: expensesObj['ItemQuantity'] || '',
      deliveryMethod: expensesObj['DeliveryMethod'] || '',
      expenses: mappedExpenses,
      sales: (() => {
        // Convert sales Map to plain object if needed
        let salesObj = bill.sales || {};
        if (salesObj instanceof Map) {
          salesObj = Object.fromEntries(salesObj);
        } else if (salesObj && typeof salesObj === 'object' && salesObj.forEach && typeof salesObj.forEach === 'function') {
          const plainObj = {};
          salesObj.forEach((val, key) => {
            plainObj[key] = val;
          });
          salesObj = plainObj;
        }
        return salesObj;
      })()
    });

    // Update expenseFields to ensure Urdu names are present
    if (setExpenseFields && defaultExpenseFields && defaultExpenseFields.length > 0) {
      let fieldsToUse = [];

      if (bill.expenseFields && Array.isArray(bill.expenseFields) && bill.expenseFields.length > 0) {
        // Merge bill's expenseFields with default fields to ensure Urdu names
        fieldsToUse = bill.expenseFields.map(billField => {
          // Find matching default field
          const defaultField = defaultExpenseFields.find(df => df.fieldName === billField.fieldName);
          // Use bill's field but ensure Urdu name from default if missing or empty
          let urduName = billField.fieldNameUrdu;
          if (!urduName || !urduName.trim()) {
            urduName = defaultField?.fieldNameUrdu || '';
          }
          // Special handling for Labor and Miscellaneous to ensure Urdu names are always set
          if (billField.fieldName === 'Labor') {
            urduName = 'مزدوری'; // Force Urdu name
          } else if (billField.fieldName === 'Miscellaneous') {
            urduName = 'مشیانہ'; // Force Urdu name
          }
          return {
            ...billField,
            fieldNameUrdu: urduName || defaultField?.fieldNameUrdu || billField.fieldName
          };
        });

        // Also add any default fields that are not in bill's expenseFields
        defaultExpenseFields.forEach(defaultField => {
          const exists = fieldsToUse.find(f => f.fieldName === defaultField.fieldName);
          if (!exists) {
            fieldsToUse.push(defaultField);
          }
        });
      } else {
        // If bill doesn't have expenseFields, use default fields
        fieldsToUse = [...defaultExpenseFields];
      }

      // Ensure Labor and Miscellaneous always have Urdu names (force override)
      fieldsToUse = fieldsToUse.map(field => {
        if (field.fieldName === 'Labor') {
          return { ...field, fieldNameUrdu: 'مزدوری' };
        } else if (field.fieldName === 'Miscellaneous') {
          return { ...field, fieldNameUrdu: 'مشیانہ' };
        }
        return field;
      });

      // Sort by order
      fieldsToUse.sort((a, b) => (a.order || 0) - (b.order || 0));
      setExpenseFields(fieldsToUse);
    } else if (setExpenseFields && defaultExpenseFields && defaultExpenseFields.length > 0) {
      // If no expenseFields in bill, use defaults immediately
      setExpenseFields(defaultExpenseFields);
    }

    // Open modal after setting all data
    setShowEditBillModal(true);
  };

  // Update bill
  const handleUpdateBill = async (e) => {
    e.preventDefault();
    if (!editingBill) return;

    try {
      setIsSaving(true);
      const token = getToken();

      // Ensure sales object is properly formatted and included
      const payload = {
        ...billFormData,
        sales: billFormData.sales || {}
      };

      const response = await api.put(`/bill/company/${editingBill._id}`, payload, {
        headers: {
          Authorization: `Bearer ${token}`
        }
      });

      toast.success(companyLanguage === 'ur' ? 'بل تبدیل ہو گیا!' : "Bill updated successfully!");
      setShowEditBillModal(false);
      setEditingBill(null);
      fetchBills();
    } catch (error) {
      console.error("Update bill error:", error);
      toast.error(error.response?.data?.message || "Failed to update bill");
    } finally {
      setIsSaving(false);
    }
  };

  // Delete bill
  const handleDeleteBill = async (billId) => {
    if (!window.confirm(companyLanguage === 'ur' ? 'کیا آپ واقعی یہ بل ختم کرنا چاہتے ہیں؟' : 'Are you sure you want to delete this bill?')) {
      return;
    }

    try {
      const token = getToken();
      await api.delete(`/bill/company/${billId}`, {
        headers: {
          Authorization: `Bearer ${token}`
        }
      });

      toast.success(companyLanguage === 'ur' ? 'بل ختم ہو گیا' : 'Bill deleted successfully!');
      fetchBills();
    } catch (error) {
      console.error("Delete bill error:", error);
      toast.error(error.response?.data?.message || "Failed to delete bill");
    }
  };

  return {
    handleBillFormChange,
    handleBillCustomerChange,
    handleExpenseChange,
    handleSalesChange,
    handleExpenseKeyDown,
    handleSalesKeyDown,
    handleCreateBill,
    handleEditBill,
    handleUpdateBill,
    handleDeleteBill
  };
};

